/**
 * Clerk · Older Stock Queue
 * Older-stock submissions awaiting clerk verification.
 * Approve bumps the component's `returned_old_stock` pool. Reject clears
 * the submission without touching stock.
 * Reuses:
 *   GET  /api/older-stock-returns?status_filter=pending_clerk_verification
 *   POST /api/older-stock-returns/{id}/verify   { decision: 'approve' | 'reject' }
 */
import React, { useMemo, useState } from 'react';
import { Archive } from 'lucide-react';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import {
  EmptyState, SearchInput, Modal, formatShortTime, shortRef,
} from '../ui';

export default function OlderQueuePage({ ctx }) {
  const { olderPending, refresh, showToast } = ctx;
  const [query, setQuery] = useState('');
  const [confirming, setConfirming] = useState(null); // { row, decision }
  const [submitting, setSubmitting] = useState(false);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return olderPending;
    return olderPending.filter((r) => {
      const hay = [
        r.component_name, r.component_size, r.submitted_by_name,
        r.submitted_by_role, r.reason, r.source_note, r.id,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [olderPending, query]);

  const doDecide = async () => {
    if (!confirming || submitting) return;
    setSubmitting(true);
    try {
      await apiClient.post(endpoints.olderStockReturns.verify(confirming.row.id), {
        decision: confirming.decision,
      });
      const verb = confirming.decision === 'approve' ? 'approved' : 'rejected';
      showToast(`Submission ${verb} · ${confirming.row.component_name}`, 'ok');
      setConfirming(null);
      await refresh.older();
      if (confirming.decision === 'approve') await refresh.catalogue();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Action failed.', 'bad');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="cp-older-page">
      <div className="cp-eyebrow">Older Stock Queue</div>
      <h1 className="cp-page-title">Older Stock Verifications</h1>
      <p className="cp-page-lede">
        Physically inspect each submission before it joins the older-stock pool. Approving
        bumps the component's <strong>Returned Old Stock</strong>; rejecting leaves inventory unchanged.
      </p>

      <div className="cp-toolbar cp-mt-5">
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="Search by component, submitter or reason…"
          testId="cp-older-search"
        />
        <div className="cp-subtle">
          {rows.length} {rows.length === 1 ? 'submission' : 'submissions'}
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          icon={Archive}
          title="No older-stock submissions waiting"
          message="Submissions from staff or students appear here for physical verification."
          testId="cp-older-empty"
        />
      ) : (
        <div className="cp-table-wrap" data-testid="cp-older-table">
          <table className="cp-table">
            <thead>
              <tr>
                <th style={{ width: '14%' }}>Submitted</th>
                <th>Component</th>
                <th style={{ width: '10%' }} className="cp-td-num">Qty</th>
                <th style={{ width: '18%' }}>Submitted By</th>
                <th>Reason / Source</th>
                <th style={{ width: '20%' }} className="cp-td-actions">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`cp-older-row-${r.id}`}>
                  <td>
                    <div className="cp-td-strong" style={{ fontSize: 13 }}>{formatShortTime(r.submitted_at)}</div>
                    <div className="cp-td-meta cp-td-mono">{shortRef(r.id, 'OLD')}</div>
                  </td>
                  <td>
                    <div className="cp-td-strong">{r.component_name || '—'}</div>
                    <div className="cp-td-meta">{r.component_size || '—'}</div>
                  </td>
                  <td className="cp-td-num cp-td-strong">{r.quantity}</td>
                  <td>
                    <div className="cp-td-strong" style={{ fontSize: 13 }}>{r.submitted_by_name || '—'}</div>
                    <div className="cp-td-meta">{r.submitted_by_role || ''}</div>
                  </td>
                  <td>
                    <div className="cp-td-meta"><strong style={{ color: 'var(--diis-ink)', fontWeight: 500 }}>Reason:</strong> {r.reason || '—'}</div>
                    <div className="cp-td-meta"><strong style={{ color: 'var(--diis-ink)', fontWeight: 500 }}>Source:</strong> {r.source_note || '—'}</div>
                  </td>
                  <td className="cp-td-actions">
                    <button
                      type="button"
                      className="cp-btn cp-btn-ghost cp-btn-sm"
                      onClick={() => setConfirming({ row: r, decision: 'reject' })}
                      data-testid={`cp-older-reject-${r.id}`}
                    >Reject</button>
                    <button
                      type="button"
                      className="cp-btn cp-btn-accent cp-btn-sm"
                      style={{ marginLeft: 8 }}
                      onClick={() => setConfirming({ row: r, decision: 'approve' })}
                      data-testid={`cp-older-approve-${r.id}`}
                    >Approve</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={!!confirming}
        onClose={() => !submitting && setConfirming(null)}
        title={confirming?.decision === 'approve' ? 'Approve Older-Stock Submission' : 'Reject Older-Stock Submission'}
        subtitle={confirming?.decision === 'approve'
          ? 'Adds the submitted quantity to the component\'s Returned Old Stock pool.'
          : 'Marks the submission rejected. No inventory is changed.'}
        testId="cp-older-confirm-modal"
        footer={(
          <>
            <button
              type="button"
              className="cp-btn cp-btn-ghost"
              onClick={() => setConfirming(null)}
              disabled={submitting}
              data-testid="cp-older-confirm-cancel"
            >Cancel</button>
            <button
              type="button"
              className={`cp-btn ${confirming?.decision === 'approve' ? 'cp-btn-accent' : 'cp-btn-danger'}`}
              onClick={doDecide}
              disabled={submitting}
              data-testid="cp-older-confirm-submit"
            >
              {submitting ? 'Working…' : (confirming?.decision === 'approve' ? 'Confirm Approve' : 'Confirm Reject')}
            </button>
          </>
        )}
      >
        {confirming && (
          <dl className="cp-def-list">
            <div><dt>Component</dt><dd>{confirming.row.component_name}</dd></div>
            <div><dt>Size</dt><dd>{confirming.row.component_size || '—'}</dd></div>
            <div><dt>Quantity</dt><dd className="cp-mono-num">{confirming.row.quantity}</dd></div>
            <div><dt>Submitted by</dt><dd>{confirming.row.submitted_by_name} · <span className="cp-subtle">{confirming.row.submitted_by_role}</span></dd></div>
          </dl>
        )}
      </Modal>
    </div>
  );
}
